import type { CompanyDetail, CompanySummary } from "@mvk/shared";
import { resolveBoothDisplayQrCode } from "@mvk/shared";
import type { Company, Hall } from "../types";
import { isApiEnabled } from "../api/config";
import { apiRequest } from "../api/client";
import { visitorAuthStorage, adminAuthStorage } from "../api/authStorage";
import { mapService, type MapViewData } from "./mapService";
import { formatHallLabel } from "../utils/locationLabel";
import { parseCompanyCategories, companyMatchesCategory } from "../utils/categoryLabel";
import { eventPackService } from "./eventPackService";
import { printableQr } from "../utils/publicQr";

export { withMapLayout } from "./mapLayout";

function mapSummaryToCompany(summary: CompanySummary, eventId: string): Company {
  return {
    id: summary.id,
    eventId,
    name: summary.name,
    category: summary.category,
    tags: [],
    hall: summary.hall ?? "",
    booth: summary.booth ?? "",
    shortAbout: summary.shortAbout,
    products: [],
    contact: {},
    logoUrl: summary.logoUrl,
    qrCodeValue: printableQr(`company:${summary.id}`),
    mapPoint: { x: 0, y: 0, hallId: "" },
  };
}

function mapDetailToCompany(detail: CompanyDetail): Company {
  const boothQr = resolveBoothDisplayQrCode(detail);
  return {
    id: detail.id,
    eventId: detail.eventId,
    name: detail.name,
    category: detail.category,
    tags: [],
    hall: detail.hall ?? "",
    booth: detail.booth ?? "",
    shortAbout: detail.shortAbout,
    products: [],
    website: detail.website,
    country: detail.country,
    city: detail.city,
    contactPersonTitle: detail.contactPersonTitle,
    contact: {
      phone: detail.phone,
      email: detail.email,
    },
    logoUrl: detail.logoUrl,
    qrCodeValue: printableQr(boothQr),
    boothQrCode: detail.boothQrCode,
    mapPoint: { x: 0, y: 0, hallId: "" },
  };
}

async function enrichBoothFromMap(company: Company, eventId: string): Promise<Company> {
  try {
    const map = await mapService.get(eventId, "visitor");
    const m = map.companies.find((c) => c.id === company.id);
    if (!m) return company;
    const booth = (company.booth || "").trim() || (m.booth || "").trim();
    const hall = (company.hall || "").trim() || (m.hall || "").trim();
    const mapPoint =
      m.mapPoint.x > 0 || m.mapPoint.y > 0 ? m.mapPoint : company.mapPoint;
    if (booth === company.booth && hall === company.hall && mapPoint === company.mapPoint) {
      return company;
    }
    return { ...company, booth, hall, mapPoint };
  } catch {
    return company;
  }
}

async function enrichListBoothFromMap(list: Company[], eventId: string): Promise<Company[]> {
  try {
    const map = await mapService.get(eventId, "visitor");
    const byId = new Map(map.companies.map((c) => [c.id, c]));
    return list.map((c) => {
      const m = byId.get(c.id);
      if (!m) return c;
      const booth = (c.booth || "").trim() || (m.booth || "").trim();
      const hall = (c.hall || "").trim() || (m.hall || "").trim();
      const mapPoint =
        m.mapPoint.x > 0 || m.mapPoint.y > 0 ? m.mapPoint : c.mapPoint;
      if (booth === c.booth && hall === c.hall && mapPoint === c.mapPoint) return c;
      return { ...c, booth, hall, mapPoint };
    });
  } catch {
    return list;
  }
}

function shuffleInPlace<T>(items: T[]): T[] {
  for (let i = items.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = items[i]!;
    items[i] = items[j]!;
    items[j] = tmp;
  }
  return items;
}

async function loadFromApi(
  eventId: string,
  limit?: number,
  order: "name" | "random" = "name",
): Promise<Company[]> {
  if (!isApiEnabled() || !visitorAuthStorage.isAuthenticated()) {
    const cached = await eventPackService.listCompanies(eventId);
    if (cached.length) {
      const mapped = cached.map(mapDetailToCompany);
      if (order === "random") shuffleInPlace(mapped);
      return limit ? mapped.slice(0, limit) : mapped;
    }
    throw new Error("[companiesService] API auth required");
  }
  try {
    const params = new URLSearchParams();
    if (limit && Number.isFinite(limit) && limit > 0) {
      params.set("limit", String(Math.min(Math.max(Math.floor(limit), 1), 5000)));
    }
    if (order === "random") params.set("order", "random");
    const qs = params.toString() ? `?${params.toString()}` : "";
    const data = await apiRequest<{ exhibitors: CompanySummary[] }>(
      `/events/${eventId}/exhibitors${qs}`,
      { audience: "visitor" },
    );
    return data.exhibitors.map((s) => mapSummaryToCompany(s, eventId));
  } catch (err) {
    const cached = await eventPackService.listCompanies(eventId);
    if (cached.length) {
      const mapped = cached.map(mapDetailToCompany);
      if (order === "random") shuffleInPlace(mapped);
      return limit ? mapped.slice(0, limit) : mapped;
    }
    throw err;
  }
}

export interface CompanyFilters {
  hall?: string;
  category?: string;
  tags?: string[];
}

export interface CompanyListOptions {
  /** Cap API / pack list size (home preview). */
  limit?: number;
  /** Skip map booth enrich (home preview — avoids map fetch). Default true when enriching. */
  enrichMap?: boolean;
  /** Home preview: random order each open so no fixed «top» companies. */
  order?: "name" | "random";
}

export const companiesService = {
  async list(
    eventId: string,
    query?: string,
    filters?: CompanyFilters,
    options?: CompanyListOptions,
  ): Promise<Company[]> {
    const order = options?.order ?? "name";
    const cached = await eventPackService.listCompanies(eventId);
    let list =
      cached.length > 0
        ? cached.map(mapDetailToCompany)
        : await loadFromApi(eventId, options?.limit, order);

    // Prefer fresh network when online, keep pack as fallback.
    if (cached.length > 0 && isApiEnabled() && visitorAuthStorage.isAuthenticated()) {
      try {
        list = await loadFromApi(eventId, options?.limit, order);
      } catch {
        /* keep pack */
        if (order === "random") shuffleInPlace(list);
      }
    } else if (cached.length > 0 && order === "random") {
      shuffleInPlace(list);
    }

    if (options?.limit && list.length > options.limit) {
      list = list.slice(0, options.limit);
    }

    if (filters?.hall) {
      list = list.filter((c) => c.hall === filters.hall);
    }
    if (filters?.category) {
      list = list.filter((c) => companyMatchesCategory(c.category, filters.category!));
    }
    if (filters?.tags?.length) {
      const tagSet = new Set(filters.tags.map((t) => t.toLowerCase()));
      list = list.filter((c) => c.tags.some((t) => tagSet.has(t.toLowerCase())));
    }
    if (query?.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.category.toLowerCase().includes(q) ||
          c.tags.some((t) => t.toLowerCase().includes(q)) ||
          c.booth.toLowerCase().includes(q) ||
          c.shortAbout.toLowerCase().includes(q),
      );
    }

    // Booth numbers from map placement override empty Tezam booths.
    if (options?.enrichMap !== false) {
      list = await enrichListBoothFromMap(list, eventId);
    }

    return list;
  },

  async getById(
    id: string,
    eventId?: string,
    audience: "visitor" | "admin" = "visitor",
  ): Promise<Company | undefined> {
    if (eventId && audience === "visitor") {
      const fromPack = await eventPackService.getCompany(eventId, id);
      if (fromPack) {
        // Try refresh in background path — sync still returns pack on fail.
        if (isApiEnabled() && visitorAuthStorage.isAuthenticated()) {
          try {
            const data = await apiRequest<{ company: CompanyDetail }>(
              `/events/${eventId}/companies/${id}`,
              { audience },
            );
            return enrichBoothFromMap(mapDetailToCompany(data.company), eventId);
          } catch {
            return enrichBoothFromMap(mapDetailToCompany(fromPack), eventId);
          }
        }
        return enrichBoothFromMap(mapDetailToCompany(fromPack), eventId);
      }
    }

    const storage = audience === "admin" ? adminAuthStorage : visitorAuthStorage;
    if (!isApiEnabled() || !storage.isAuthenticated() || !eventId) {
      throw new Error("[companiesService] API auth and eventId required");
    }
    try {
      const data = await apiRequest<{ company: CompanyDetail }>(
        `/events/${eventId}/companies/${id}`,
        { audience },
      );
      return enrichBoothFromMap(mapDetailToCompany(data.company), eventId);
    } catch {
      return undefined;
    }
  },

  async updateProfile(
    eventId: string,
    companyId: string,
    input: {
      name?: string;
      category?: string;
      shortAbout?: string;
      phone?: string;
      email?: string;
      website?: string;
      country?: string;
      city?: string;
      contactPersonTitle?: string;
    },
  ): Promise<Company | undefined> {
    if (!isApiEnabled() || !adminAuthStorage.isAuthenticated()) {
      throw new Error("[companiesService] admin API required");
    }
    const data = await apiRequest<{ company: CompanyDetail }>(
      `/events/${eventId}/companies/${companyId}`,
      { method: "PATCH", body: input, audience: "admin" },
    );
    return mapDetailToCompany(data.company);
  },

  async getHalls(eventId: string): Promise<Hall[]> {
    const companies = await this.list(eventId);
    const hallLabels = new Set(companies.map((c) => c.hall).filter(Boolean));
    return Array.from(hallLabels).map((label) => ({
      id: `hall-${eventId}-${label}`,
      eventId,
      name: formatHallLabel(label),
      label,
      floor: 1,
    }));
  },

  async getTags(eventId: string): Promise<string[]> {
    const companies = await this.list(eventId);
    const tags = new Set<string>();
    companies.forEach((c) => c.tags.forEach((t) => tags.add(t)));
    return Array.from(tags).sort();
  },

  async getCategories(eventId: string): Promise<string[]> {
    const companies = await this.list(eventId);
    const cats = new Set<string>();
    companies.forEach((c) => {
      for (const label of parseCompanyCategories(c.category)) cats.add(label);
    });
    return Array.from(cats).sort((a, b) => a.localeCompare(b, "ru"));
  },

  async listForMap(eventId: string): Promise<MapViewData> {
    return mapService.get(eventId, "visitor");
  },
};
